import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment';
import { OrganizationStructureService } from '@app/modules/organization-structure/services/organization-structure.service';
import { OrganizationalChartItem } from '@app/modules/organization-structure/models/organization-structure.model';
import { PositionsService } from '@app/modules/positions/services/positions.service';
import { PositionChartItem } from '@app/modules/positions/models/positions.model';
import { ChartNode, ChartSearchItem, PositionFilterDefinition } from '../models/chart-node.model';
import { OrgChartFields } from '../models/org-chart-fields';

export const INITIAL_EXPAND_LEVEL = 2;

// Natural order, so "Engineer 2" comes before "Engineer 10"
export function compareNames(a: ChartNode, b: ChartNode): number {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

@Injectable({
  providedIn: 'root'
})
export class OrgChartV2DataService {

  readonly fields: OrgChartFields;

  constructor(
    translate: TranslateService,
    private organizationStructureService: OrganizationStructureService,
    private positionsService: PositionsService,
  ) {
    this.fields = new OrgChartFields(translate);
  }

  // Loading

  loadOrganizations(): Observable<ChartNode[]> {
    return this.organizationStructureService.getOrganizationalChart('true').pipe(
      map(items => this.buildTree(items.map(item => this.organizationToNode(item))))
    );
  }

  loadPositions(): Observable<ChartNode[]> {
    return this.positionsService.getPositionChart().pipe(
      map(items => {
        const nodes = items.map(item => this.positionToNode(item));
        const roots = this.buildTree(nodes);
        // Expired positions are removed together with everything below them, as in the original chart
        return this.removeWhere(roots, node => node.data[this.fields.POSITION.status] === 'Expired');
      })
    );
  }

  organizationToNode(item: OrganizationalChartItem): ChartNode {
    const f = this.fields.ORGANIZATION;
    const extra = item as any;
    const node = this.createNode('organization', item.id, item.parent?.id ?? null, item.name, {
      [f.name]: item.name,
      [f.startDate]: this.formatDate(extra.startDate),
      [f.endDate]: this.formatDate(extra.endDate),
      [f.organizationType]: extra.organizationType?.name ?? null,
    });

    node.employees = (item.employees ?? [])
      .filter(emp => this.isActiveEmployee(emp.status))
      .map(emp => {
        const positionNames = emp.positions?.length ? emp.positions.map(p => p.name).join(', ') : undefined;
        return this.employeeToNode(node, emp.id, emp.firstName, emp.lastName, emp.profilePhoto, positionNames);
      });

    return node;
  }

  positionToNode(item: PositionChartItem): ChartNode {
    const f = this.fields.POSITION;
    // getPositionChart only returns a subset of the position; read the optional extras defensively
    const extra = item as any;

    const employees = (item.employees ?? []).filter(emp => this.isActiveEmployee(emp.status));
    const slots: number | undefined = extra.numberOfSlots || undefined;
    const isVacant = slots ? slots > employees.length : employees.length === 0;
    const isExpired = !!extra.endDate && new Date(extra.endDate) < new Date();

    const node = this.createNode('position', item.id, item.parent?.id ?? null, item.name, {
      [f.positionName]: item.name,
      [f.jobTitle]: extra.functionalJobTitle?.text ?? null,
      [f.marketPositionTitle]: extra.marketPositionTitle?.text ?? null,
      [f.marketPosition]: extra.marketPosition?.text ?? null,
      [f.marketView]: extra.marketView?.text ?? null,
      [f.unionCode]: extra.unionCode?.text ?? null,
      [f.shiftCode]: extra.shiftCode?.text ?? null,
      [f.numberSlots]: slots ?? null,
      [f.enforceSlotLimit]: extra.enforceSlotLimit ?? null,
      [f.department]: item.department?.text ?? null,
      [f.division]: item.division?.text ?? null,
      [f.empCategory]: item.employeeCategory?.text ?? null,
      [f.empGroup]: item.employmentGroup?.text ?? null,
      [f.empType]: item.employmentType?.text ?? null,
      [f.jobBand]: extra.jobBand?.text ?? null,
      [f.jobFamily]: extra.jobFamily?.text ?? null,
      [f.jobGroup]: extra.jobGroup?.text ?? null,
      [f.jobType]: extra.jobType?.text ?? null,
      [f.projectTeam]: item.projectTeam?.text ?? null,
      [f.region]: item.region?.text ?? null,
      [f.country]: item.country?.name ?? null,
      [f.workLocation]: item.workLocation?.name ?? null,
      [f.subDivision]: extra.subDivision?.text ?? null,
      [f.workRotation]: extra.workRotation?.name ?? null,
      [f.workRotationStart]: this.formatDate(extra.workRotationStartDate),
      [f.isVacant]: isVacant ? 'Vacant' : 'Occupied',
      [f.startDate]: this.formatDate(extra.startDate),
      [f.endDate]: this.formatDate(extra.endDate),
      [f.status]: isExpired ? 'Expired' : 'Active',
    });
    node.subtitle = item.department?.text ?? undefined;
    node.isVacant = isVacant;
    node.slots = slots;
    node.employees = employees.map(emp => this.employeeToNode(node, emp.id, emp.firstName, emp.lastName, emp.profilePhoto));

    return node;
  }

  employeeToNode(parent: ChartNode, id: string, firstName: string, lastName: string, photo?: string, positionNames?: string): ChartNode {
    const f = this.fields.EMPLOYEE;
    const name = [firstName, lastName].filter(Boolean).join(' ');
    const node = this.createNode('employee', id, parent.id, name, {
      [f.name]: name,
      [f.firstName]: firstName,
      [f.lastName]: lastName,
      [f.positions]: positionNames ?? null,
    });
    node.uid = `${parent.id}:${id}`;
    node.subtitle = positionNames;
    node.photo = photo || undefined;
    return node;
  }

  createNode(type: ChartNode['type'], id: string, parentId: string | null, name: string, data: Record<string, any>): ChartNode {
    return {
      uid: id,
      id,
      parentId,
      type,
      name: name ?? '',
      data,
      employees: [],
      children: [],
      expanded: false,
      employeesExpanded: false,
    };
  }

  // Tree helpers

  // Links nodes by parentId. Nodes whose parent is missing become roots.
  buildTree(nodes: ChartNode[]): ChartNode[] {
    const byId = new Map<string, ChartNode>();
    nodes.forEach(node => byId.set(node.id, node));

    const roots: ChartNode[] = [];
    nodes.forEach(node => {
      const parent = node.parentId ? byId.get(node.parentId) : undefined;
      if (parent && parent !== node) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Guard against cycles in the data: anything not reachable from a root is promoted to a root
    const reachable = new Set<ChartNode>();
    this.walk(roots, node => { reachable.add(node); });
    nodes.filter(node => !reachable.has(node)).forEach(node => {
      if (!reachable.has(node)) {
        const parent = byId.get(node.parentId);
        parent.children = parent.children.filter(child => child !== node);
        roots.push(node);
        this.walk([node], descendant => { reachable.add(descendant); });
      }
    });

    this.sortTree(roots);
    this.expandToLevel(roots, INITIAL_EXPAND_LEVEL);
    return roots;
  }

  sortTree(nodes: ChartNode[]) {
    nodes.sort(compareNames);
    nodes.forEach(node => {
      node.employees.sort(compareNames);
      this.sortTree(node.children);
    });
  }

  // Visits nodes depth first (employees excluded). Returning false from fn skips a node's children.
  walk(nodes: ChartNode[], fn: (node: ChartNode, depth: number, parent: ChartNode | null) => boolean | void, depth = 0, parent: ChartNode | null = null) {
    for (const node of nodes) {
      if (fn(node, depth, parent) !== false) {
        this.walk(node.children, fn, depth + 1, node);
      }
    }
  }

  expandToLevel(roots: ChartNode[], level: number) {
    this.walk(roots, (node, depth) => { node.expanded = depth < level - 1; });
  }

  setExpanded(nodes: ChartNode[], expanded: boolean) {
    this.walk(nodes, node => { node.expanded = expanded; });
  }

  findByUid(roots: ChartNode[], uid: string): ChartNode | null {
    let found: ChartNode | null = null;
    this.walk(roots, node => {
      if (found) {
        return false;
      }
      if (node.uid === uid) {
        found = node;
      } else {
        found = node.employees.find(emp => emp.uid === uid) ?? null;
      }
    });
    return found;
  }

  // Chain of nodes from a root down to (and including) the node with this uid.
  findPath(roots: ChartNode[], uid: string): ChartNode[] {
    for (const node of roots) {
      if (node.uid === uid) {
        return [node];
      }
      const employee = node.employees.find(emp => emp.uid === uid);
      if (employee) {
        return [node, employee];
      }
      const path = this.findPath(node.children, uid);
      if (path.length) {
        return [node, ...path];
      }
    }
    return [];
  }

  isSameOrDescendant(ancestor: ChartNode, node: ChartNode): boolean {
    let found = false;
    this.walk([ancestor], current => {
      if (current === node) {
        found = true;
      }
      return !found;
    });
    return found;
  }

  descendantCount(node: ChartNode): number {
    let count = -1;
    this.walk([node], () => { count++; });
    return count;
  }

  removeWhere(roots: ChartNode[], predicate: (node: ChartNode) => boolean): ChartNode[] {
    const kept = roots.filter(node => !predicate(node));
    kept.forEach(node => { node.children = this.removeWhere(node.children, predicate); });
    return kept;
  }

  // Detaches a node (or an employee entry) from wherever it sits in the tree.
  detach(roots: ChartNode[], target: ChartNode): ChartNode[] {
    const remaining = roots.filter(node => node !== target);
    this.walk(remaining, node => {
      node.children = node.children.filter(child => child !== target);
      node.employees = node.employees.filter(emp => emp !== target);
    });
    return remaining;
  }

  // Search

  buildSearchIndex(roots: ChartNode[]): ChartSearchItem[] {
    const items: ChartSearchItem[] = [];
    this.walk(roots, (node, _depth, parent) => {
      items.push({ node, label: node.name, hint: parent ? parent.name : '' });
      node.employees.forEach(emp => {
        items.push({ node: emp, label: emp.name, hint: emp.subtitle || node.name });
      });
    });
    return items;
  }

  search(index: ChartSearchItem[], term: string, limit = 50): ChartSearchItem[] {
    const needle = (term ?? '').trim().toLowerCase();
    if (!needle) {
      return [];
    }
    const starts: ChartSearchItem[] = [];
    const contains: ChartSearchItem[] = [];
    for (const item of index) {
      const label = item.label.toLowerCase();
      if (label.startsWith(needle)) {
        starts.push(item);
      } else if (label.includes(needle) || item.hint.toLowerCase().includes(needle)) {
        contains.push(item);
      }
    }
    return [...starts, ...contains].slice(0, limit);
  }

  // Filters

  filterDefinitions(roots: ChartNode[]): PositionFilterDefinition[] {
    return this.fields.POSITION_FILTERS.map(key => {
      const values = new Set<string>();
      this.walk(roots, node => {
        const value = node.data[key];
        if (value !== null && value !== undefined && value !== '') {
          values.add(String(value));
        }
      });
      return { key, label: key, options: Array.from(values).sort((a, b) => a.localeCompare(b)) };
    });
  }

  nodeMatchesFilters(node: ChartNode, filters: Record<string, string[]>): boolean {
    return Object.entries(filters).every(([key, values]) =>
      !values?.length || values.includes(String(node.data[key]))
    );
  }

  // Marks nodes that don't match the filters as hidden, unless a descendant matches, in which
  // case they stay visible but dimmed for context. Ancestors of matches are expanded.
  // Returns the uids of nodes that should be rendered.
  applyFilters(roots: ChartNode[], filters: Record<string, string[]>): Set<string> | null {
    const active = Object.values(filters).some(values => values?.length);
    this.walk(roots, node => { node.dimmed = false; });
    if (!active) {
      return null;
    }

    const visible = new Set<string>();
    const visit = (node: ChartNode): boolean => {
      let childVisible = false;
      node.children.forEach(child => {
        if (visit(child)) {
          childVisible = true;
        }
      });
      const matches = this.nodeMatchesFilters(node, filters);
      if (matches || childVisible) {
        visible.add(node.uid);
        node.dimmed = !matches;
        if (childVisible) {
          node.expanded = true;
        }
        return true;
      }
      return false;
    };
    roots.forEach(visit);
    return visible;
  }

  // Misc

  isActiveEmployee(status: any): boolean {
    if (!status) {
      return true;
    }
    if (typeof status === 'string') {
      return status === 'Active';
    }
    return status.id === 'Active';
  }

  formatDate(date: any): string | null {
    return date ? moment(date).format('DD-MMM-YYYY') : null;
  }
}
